import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import CasesRelacionados from '@/pages/cases/components/CasesRelacionados';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const serviceAreas = [
  { icon: 'ri-fire-line', title: 'Caldeira, recuperação e utilidades' },
  { icon: 'ri-drop-line', title: 'Secagem' },
  { icon: 'ri-file-paper-line', title: 'Linha de fibras' },
  { icon: 'ri-tree-line', title: 'Área de madeira' },
];

const specialties = [
  'Mecânica geral',
  'Manutenção de equipamentos rotativos',
  'Manutenção de equipamentos estáticos',
  'Caldeiraria e solda de apoio',
  'Lubrificação industrial',
  'Manutenção hidráulica',
  'Filtragem de óleo hidráulico',
  'Limpeza de unidades hidráulicas',
  'Reparo hidráulico em campo',
  'Troca de vedações e componentes hidráulicos',
];

const differentials = [
  { icon: 'ri-user-settings-line', title: 'Equipe técnica experiente' },
  { icon: 'ri-building-2-line', title: 'Atuação em ambiente industrial' },
  { icon: 'ri-shield-check-line', title: 'Foco em segurança' },
  { icon: 'ri-time-line', title: 'Compromisso com prazo' },
  { icon: 'ri-team-line', title: 'Apoio multidisciplinar' },
  { icon: 'ri-flashlight-line', title: 'Resposta rápida' },
  { icon: 'ri-file-paper-line', title: 'Experiência em papel e celulose' },
  { icon: 'ri-settings-3-line', title: 'Suporte mecânico e hidráulico' },
];

export default function Servicos() {
  useSeo({
    title: 'Paradas de Manutenção Industrial | Serviços de Campo | Pilar',
    description: 'Atuação em paradas programadas e paradas gerais de manutenção industrial: mecânica, hidráulica, caldeiraria, solda e lubrificação industrial em plantas de papel e celulose, mineração, portos e energia.',
    keywords: 'paradas de manutenção industrial, paradas programadas, paradas gerais, mecânica industrial, hidráulica industrial, caldeiraria, soldagem, lubrificação industrial, manutenção industrial, papel e celulose, Aracruz, ES',
    canonical: 'https://www.pilarmanutencao.com.br/paradas-manutencao-industrial',
    ogUrl: 'https://www.pilarmanutencao.com.br/paradas-manutencao-industrial',
    ogImage: ASSETS.logoPilar,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/SERVICOS-DE-CAMPO/HERO/HERO-SERVICOS-CAMPO.webp"
              alt="Serviços de campo e manutenção industrial em plantas de papel e celulose"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">
                  Serviços de Campo
                </span>
              </div>
              <h1 className="font-display text-4xl md:text-6xl font-bold text-white uppercase leading-tight mb-6">
                Paradas de{' '}
                <span className="text-gp-yellow">manutenção industrial</span>
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Atuação em paradas programadas e paradas gerais com mecânica industrial, hidráulica, caldeiraria, solda e lubrificação, oferecendo apoio técnico completo e recuperação de equipamentos em campo.
              </p>
            </div>
          </div>
        </section>

        {/* Serviços de Campo — Links */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Nossos Serviços de Campo
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Link to="/mecanica-industrial" className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-gp-yellow rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-settings-3-line text-gp-graphite text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                  Mecânica Industrial
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Montagem, desmontagem, alinhamento e apoio técnico mecânico em campo.
                </p>
                <span className="text-gp-yellow text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                  Saiba mais
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </span>
              </Link>

              <Link to="/hidraulica-industrial" className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-gp-yellow rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-drop-line text-gp-graphite text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                  Hidráulica Industrial
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Diagnóstico, troca de mangueiras, correção de vazamentos e apoio técnico.
                </p>
                <span className="text-gp-yellow text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                  Saiba mais
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </span>
              </Link>

              <Link to="/caldeiraria-solda" className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-gp-yellow rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-fire-line text-gp-graphite text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                  Caldeiraria e Solda
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Soldagem, recuperação estrutural, fabricação de componentes e reforços.
                </p>
                <span className="text-gp-yellow text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                  Saiba mais
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </span>
              </Link>

              <Link to="/lubrificacao-industrial" className="group bg-white/5 border border-white/10 rounded-lg p-6 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300 cursor-pointer">
                <div className="w-14 h-14 bg-gp-yellow rounded-sm flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                  <i className="ri-oil-line text-gp-graphite text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                  Lubrificação Industrial
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  Lubrificação preventiva, corretiva, gestão de pontos e controle de contaminação.
                </p>
                <span className="text-gp-yellow text-sm font-medium uppercase tracking-wider flex items-center gap-2">
                  Saiba mais
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </span>
              </Link>
            </div>
          </div>
        </section>

        {/* Intro */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div
              className="absolute top-0 left-0 w-full h-full"
              style={{
                backgroundImage:
                  'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
                backgroundSize: '40px 40px',
              }}
            ></div>
          </div>
          <div className="relative z-10 px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Sobre os Serviços</span>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-4">
                O Grupo Pilar atua como parceiro técnico em paradas de manutenção industrial, oferecendo apoio especializado em paradas programadas e paradas gerais para plantas de papel e celulose, mineração, portos e energia.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Nossos serviços de campo incluem mecânica industrial, hidráulica industrial, caldeiraria e solda, lubrificação industrial, apoio à manutenção industrial e recuperação de equipamentos em campo, sempre com foco em segurança, qualidade, prazo e resposta técnica.
              </p>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Atuação no setor de celulose e papel
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {serviceAreas.map((area, index) => (
                <div
                  key={index}
                  className="group bg-white/5 border border-white/10 rounded-lg p-5 flex items-center gap-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gp-yellow rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className={`${area.icon} text-gp-graphite text-xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <span className="text-white font-semibold text-sm uppercase tracking-wider">
                    {area.title}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hydraulic Block */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0">
                <img
                  src="/images/SERVICOS-DE-CAMPO/HIDRAULICA-INDUSTRIAL/HERO/HERO-HIDRAULICA-INDUSTRIAL.webp"
                  alt="Serviços hidráulicos em campo durante paradas industriais"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gp-graphite/80"></div>
              </div>
              <div className="relative z-10 p-8 md:p-12">
                <div className="max-w-3xl">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-[3px] bg-gp-yellow"></div>
                    <span className="text-gp-yellow text-sm uppercase tracking-wider font-semibold">
                      Especialização
                    </span>
                  </div>
                  <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                    Hidráulica industrial em campo
                  </h2>
                  <p className="text-gray-300 leading-relaxed">
                    Durante paradas gerais e intervenções programadas, o Grupo Pilar também atua com serviços hidráulicos em campo, incluindo filtragem de óleo, limpeza de unidades hidráulicas, avaliação de sistemas, troca de vedações, substituição de componentes e pequenos reparos necessários para restabelecer a confiabilidade dos equipamentos.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Technical Specialties */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">
                Especialidades técnicas
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {specialties.map((spec, index) => (
                <div
                  key={index}
                  className="bg-white/5 border border-white/10 rounded-lg p-4 text-center hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <span className="font-display text-sm text-gray-100 uppercase tracking-wider leading-relaxed">
                    {spec}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Differentials */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="text-center mb-12">
              <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase">
                Por que contar com o <span className="text-gp-yellow">Grupo Pilar</span> em paradas gerais?
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {differentials.map((diff, index) => (
                <div
                  key={index}
                  className="group bg-white/5 border border-white/10 rounded-lg p-5 text-center hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
                >
                  <div className="w-14 h-14 bg-gp-yellow rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform duration-300">
                    <i className={`${diff.icon} text-gp-graphite text-xl w-7 h-7 flex items-center justify-center`}></i>
                  </div>
                  <p className="text-white text-sm font-semibold uppercase tracking-wider">
                    {diff.title}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa de apoio técnico para parada geral ou manutenção industrial?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Fale com nossa equipe e conte com o Grupo Pilar para apoiar sua operação com serviços de campo, manutenção mecânica, hidráulica e suporte técnico especializado.
                </p>
                <a
                  href="https://wa.me/5527993092922"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar atendimento técnico</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Cases Relacionados */}
        <CasesRelacionados serviceSlugs={['mecanica-industrial', 'hidraulica-industrial']} />

        {/* Back to Home */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider"
              >
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Voltar para a Home
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}